'use client';

import InlineStyle from '@/components/InlineStyle';
import { motion } from 'framer-motion';
import { Activity, BarChart3, Clock, MessageSquareWarning, Radar, ShieldCheck, Sparkles, Target, TrendingUp, Zap } from 'lucide-react';
import { Button, Card, CheckList, FaqAccordion, PageHero, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import BookDemoSection from '@/components/BookDemoSection';
import { fadeUp, staggerContainer, viewportOnce } from '@/lib/motion';

// AUTO-GENERATED by the on-brand page generator. Real components only.
const ICONS = [Radar, Clock, Activity, MessageSquareWarning, TrendingUp, ShieldCheck, Sparkles, Target, BarChart3, Zap];
const METRICS = [{"value":"95%","label":"Customer Complaint Resolution Rate"},{"value":"30min","label":"Average Response Time to Customer Complaints"},{"value":"25%","label":"Increase in Customer Satisfaction"}];
const PROBLEMS = ["Difficulty in monitoring social media and online reviews","Inadequate resources to respond to customer complaints","Lack of visibility into customer sentiment and feedback","Inability to measure the effectiveness of complaint resolution"];
const CAPABILITIES = [{"title":"Social Media Monitoring","body":"Track social media conversations about your bank and respond to customer complaints in real-time"},{"title":"Customer Sentiment Analysis","body":"Analyze customer feedback and sentiment to identify areas for improvement"},{"title":"Complaint Resolution Management","body":"Streamline complaint resolution processes and measure effectiveness"},{"title":"Reputation Reporting and Analytics","body":"Get detailed reports and analytics on your bank's reputation and customer satisfaction"},{"title":"AI-Powered Insights","body":"Receive actionable insights and recommendations to improve customer experience and reputation"},{"title":"Integration with Existing Systems","body":"Seamlessly integrate our platform with your existing customer relationship management systems"}];
const FAQS = [{"q":"How does your platform help banks monitor brand reputation?","a":"Our platform uses AI-powered social media monitoring and sentiment analysis to track conversations about your bank and provide insights on customer feedback"},{"q":"Can your platform help banks respond to customer complaints in real-time?","a":"Yes, our platform provides banks with the tools and insights needed to respond to customer complaints in real-time, improving customer satisfaction and loyalty"},{"q":"How does your platform measure the effectiveness of complaint resolution?","a":"Our platform provides detailed reports and analytics on complaint resolution rates, response times, and customer satisfaction, helping banks measure the effectiveness of their complaint resolution processes"},{"q":"Is your platform customizable to meet the specific needs of our bank?","a":"Yes, our platform is highly customizable and can be tailored to meet the specific needs and requirements of your bank"}];

export default function GeneratedBody() {
  return (
    <div>
      <span className="sr-only">{"Monitor brand reputation and respond to complaints in real time, improve customer experience with our AI-powered platform, try now"}</span>

      {/* 1 — Hero */}
      <PageHero
        align="center"
        eyebrow={"Transform Your Customer Experience"}
        title={<>{"Unlock "}<span className="serif-accent italic">{"Excellence"}</span>{" in Banking"}</>}
        subtitle={"Stay ahead of the competition with real-time brand reputation monitoring and complaint resolution"}
        actions={<Button variant="cta" size="lg" href="#book-demo">{"Get Started Today"}</Button>}
      />

      {/* 2 — Proof metrics */}
      <MetricsBand items={METRICS} columns={3} />

      {/* 3 — Problem / solution */}
      <PageSection bg="tint">
        <div className="lp-split">
          <SectionTitle
            align="left"
            eyebrow={"The Challenge"}
            title={<>{"Managing "}<span className="serif-accent italic">{"Reputation"}</span>{" in a Digital Age"}</>}
            subtitle={"Banks face numerous challenges in maintaining a positive brand reputation and responding to customer complaints in a timely manner"}
          />
          <Card padding="xl" radius="2xl">
            <CheckList items={PROBLEMS} />
          </Card>
        </div>
      </PageSection>

      {/* 4 — Capability cards */}
      <PageSection bg="page">
        <SectionTitle
          align="center"
          eyebrow={"Our Solution"}
          title={<>{"Powering "}<span className="serif-accent italic">{"Intelligence"}</span>{" in Bank Reputation Management"}</>}
          subtitle={"Our AI-powered platform provides banks with the tools and insights needed to monitor brand reputation and respond to customer complaints in real-time"}
        />
        <motion.div className="lp-grid" variants={staggerContainer(0.08)} initial="hidden" whileInView="show" viewport={viewportOnce}>
          {CAPABILITIES.map((cap, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <motion.div key={cap.title} variants={fadeUp}>
                <Card padding="xl" radius="2xl" hover className="lp-card">
                  <span className="lp-icon" aria-hidden="true">
                    <Icon size={22} color="var(--brand)" strokeWidth={2.2} />
                  </span>
                  <h3 className="lp-card-title">{cap.title}</h3>
                  <p className="lp-card-body">{cap.body}</p>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </PageSection>

      {/* 5 — FAQ */}
      <PageSection id="faqs" bg="tint">
        <SectionTitle align="center" title={<>{"Frequently Asked "}<span className="serif-accent italic">{"Questions"}</span>{""}</>} />
        <FaqAccordion items={FAQS} columns={2} name="generated-faq" />
      </PageSection>

      {/* 6 — Book a demo (always last) */}
      <BookDemoSection />

      <InlineStyle css={`
        .lp-split { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
        .lp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .lp-card { height: 100%; }
        .lp-icon { display: inline-flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: var(--radius-lg); background: var(--brand-subtle); border: 1px solid var(--brand-border); margin-bottom: 16px; }
        .lp-card-title { font-size: 19px; font-weight: 700; color: var(--text-heading, #0D1B3E); margin: 0 0 8px; letter-spacing: -0.01em; }
        .lp-card-body { font-size: 14.5px; line-height: 1.65; color: var(--text-body); margin: 0; }
        @media (max-width: 900px) { .lp-split { grid-template-columns: 1fr; gap: 32px; } .lp-grid { grid-template-columns: 1fr; } }
      `} />
    </div>
  );
}
